import type { GanttProjectRow, GanttUserRow } from 'components/gantt/gantt-types';
import { EMPTY_ARRAY } from 'constants/defaults';
import { mapValues } from 'lodash-es';
import { getAllLoadedProjects } from 'redux/selectors/projects';
import { getUserRoleById } from 'redux/selectors/roles';
import { getTeams } from 'redux/selectors/teams';
import { createSelector } from 'reselect';
import type { Forecast } from 'routes/forecast/types/forecast';
import { filterForecastsByQuery } from 'routes/forecast/utils/forecast';
import { naturalCompare } from 'utils/naturalSort';
import { getProjectColor } from 'utils/project';
import {
  getFilteredForecastUsers,
  getForecastQuery,
  getForecastQueryUserIds,
  getPlainForecastsByAssignee,
  getPlainForecastsByProject,
  UNASSIGNED_FORECAST_GROUP_KEY,
} from './forecast.selectors';

const getFilteredForecastsByAssignee = createSelector(
  [getPlainForecastsByAssignee, getForecastQuery, getTeams],
  (forecastsByAssignee, query, teams) => {
    return mapValues(forecastsByAssignee, (forecasts, userId) =>
      filterForecastsByQuery({
        forecasts,
        query: {
          ...query,
          userIds: userId === UNASSIGNED_FORECAST_GROUP_KEY ? [] : [Number(userId)],
          since: null,
          until: null,
        },
        teams,
        onlyMarkAsFiltered: true,
      })
    );
  }
);

const getFilteredForecastsByProject = createSelector(
  [getPlainForecastsByProject, getForecastQuery, getTeams],
  (forecastsByProject, query, teams) => {
    return mapValues(forecastsByProject, (forecasts, projectId) =>
      filterForecastsByQuery({
        forecasts,
        query: { ...query, projectIds: [Number(projectId)], since: null, until: null },
        teams,
        onlyMarkAsFiltered: true,
      })
    );
  }
);

export const getSelectedUsersWithForecasts = createSelector(
  [getFilteredForecastUsers, getForecastQueryUserIds, getFilteredForecastsByAssignee],
  (users, filteredUserIds, forecastsById) => {
    return [null, ...users]
      .filter(user => filteredUserIds.length < 1 || filteredUserIds.includes(user?.id ?? null))
      .map(user => {
        const forecasts = forecastsById[user?.id ?? UNASSIGNED_FORECAST_GROUP_KEY] ?? EMPTY_ARRAY;
        return { user, forecasts };
      });
  }
);

export const getSelectedProjectsWithForecasts = createSelector(
  [getAllLoadedProjects, getForecastQuery, getFilteredForecastsByProject],
  (projects, query, forecastsById) => {
    return projects
      .filter(project => {
        // For forecast, projectIds is always Array<number>
        if (query.projectIds.length === 0) {
          return project.base.active; // Show active projects by default when none selected
        }
        return query.projectIds.includes(project.base.id);
      })
      .map(project => {
        const forecasts = forecastsById[project.base.id] ?? EMPTY_ARRAY;
        return { project, forecasts };
      });
  }
);

const forecastToTaskBase = (forecast: Forecast & { hiddenByFilter?: boolean }) => ({
  id: forecast.id,
  startDate: forecast.from,
  endDate: forecast.to ?? forecast.from,
  title: forecast.title,
  manage: forecast.manage,
  progress:
    forecast.logged_duration.total_minutes && forecast.users.length > 0
      ? +((forecast.logged_duration.total_minutes / forecast.estimated_minutes) * 100).toFixed(2)
      : 0,
  completed: forecast.completed_at != null,
  estimated_minutes: forecast.estimated_minutes,
  estimated_minutes_per_user: forecast.estimated_minutes / (forecast.users.length || 1),
  hiddenByFilter: !!forecast.hiddenByFilter,
});

export const getForecastTimelineUserRows = createSelector(
  [getSelectedUsersWithForecasts, getUserRoleById],
  (users, userRoleById) => {
    return users.map(({ user, forecasts }): GanttUserRow => {
      return {
        type: 'user',
        userId: user?.id ?? null,
        userRole: userRoleById[user?.role_id!]?.display_name ?? null,
        name: user?.name ?? 'Unassigned',
        avatar: user?.avatar.large_retina,
        tasks: forecasts.map(forecast => ({
          ...forecastToTaskBase(forecast),
          type: 'user',
          projectName: forecast.project?.name ?? '?',
          displayColor: getProjectColor(forecast.project),
          projectArchived: forecast.project?.active === false,
        })),
      };
    });
  }
);

export const getForecastTimelineProjectRows = createSelector(
  [getSelectedProjectsWithForecasts],
  projects => {
    const projectRows = projects.map(({ project, forecasts }): GanttProjectRow => {
      return {
        type: 'project',
        projectId: project.base.id,
        displayColor: getProjectColor(project.base),
        name: project.base.name,
        clientName: project.base.client.name,
        projectArchived: project.base.active === false,
        tasks: forecasts.map(forecast => ({
          ...forecastToTaskBase(forecast),
          type: 'project',
          users:
            forecast.users.length > 0
              ? forecast.users.map(u => ({
                  userName: u.name ?? 'Unassigned',
                  avatar: u.avatar.large_retina ?? '',
                }))
              : [],
          projectArchived: project.base.active === false,
        })),
      };
    });
    projectRows.sort(
      (a, b) => naturalCompare(a.clientName, b.clientName) || naturalCompare(a.name, b.name)
    );
    return projectRows;
  }
);
